import { useDebounceFn, useEventListener } from '@vueuse/core';

type Handler<E extends Event = Event> = (event: E) => void;

type EventEntry<E extends Event = Event>
    = | Handler<E>
        | { handler: Handler<E>, debounce?: number };

function register<E extends Event>(event: string, entry: EventEntry<E>) {
    const isObject = typeof entry === 'object' && 'handler' in entry;
    const handler = isObject ? entry.handler : entry;
    const debounce = isObject ? entry.debounce : undefined;

    const listener = debounce !== undefined
        ? useDebounceFn(handler as Handler, debounce)
        : handler;

    return useEventListener(window, event, listener as EventListener);
}

export function useWindowEvents(handlers: {
    resize?: EventEntry<UIEvent>
    scroll?: EventEntry<Event>
    [key: string]: EventEntry<any> | undefined
}) {
    const stops = Object.entries(handlers)
        .filter(([, entry]) => entry)
        .map(([event, entry]) => register(event, entry!));

    return () => stops.forEach((stop) => stop());
}

export function useWindowResize(
    handler: Handler<UIEvent>,
    options: { debounce?: number } = {},
) {
    return register<UIEvent>('resize', { handler, debounce: 150, ...options });
}

export function useWindowScroll(
    handler: Handler<Event>,
    options: { debounce?: number } = {},
) {
    return register('scroll', { handler, debounce: 150, ...options });
}
